/**
 * The interests normaliser (PPL-490).
 *
 * `users/{uid}.interests` is canonically a `string[]` of lowercased, trimmed,
 * deduped tags. It shipped for years as a comma-separated STRING — the chip
 * editors joined their tags with ", " — so docs written by any build before
 * this one still hold that shape, and every reader has to accept both.
 *
 * This file is a deliberate DUPLICATE of
 * cloud-functions/functions/src/profile/interests.ts (and of f8-skills'
 * interests.js and the app's src/utils/interests.ts). The four packages are built
 * and shipped separately and share no runtime, so the copies are kept
 * equivalent in behaviour rather than shared. Any change here changes there.
 *
 * NOT this field: `users/{uid}/interests/{docId}` (the hobbies subcollection
 * the app writes) — different data, left alone.
 */

/**
 * Coerce any stored `interests` value to the canonical array.
 *
 * - string → split on ",", trim, lowercase, drop blanks, dedupe
 * - array  → same cleaning per string item; non-string items are ignored.
 *            Array items are NOT re-split on "," — an array is already a list.
 * - anything else (null, undefined, number, object) → []
 *
 * Order is preserved (first occurrence wins on a duplicate).
 */
export function normalizeInterests(value: unknown): string[] {
  let items: unknown[];
  if (typeof value === 'string') {
    items = value.split(',');
  } else if (Array.isArray(value)) {
    items = value;
  } else {
    return [];
  }

  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    if (typeof item !== 'string') continue;
    const tag = item.trim().toLowerCase();
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
  }
  return out;
}

/** Convenience for the one-line renderers that show interests as prose. */
export function formatInterests(value: unknown, separator = ', '): string {
  return normalizeInterests(value).join(separator);
}

/**
 * PPL-490 phase 1 — what the editors WRITE.
 *
 * Every reader on every surface now accepts both shapes, but app builds
 * already in the field still `.split(',')` this field on their own profile,
 * and a user who edits on web then opens the old app would hit that. Until
 * the app release carrying the two-shape readers is in the stores, the
 * editors keep writing the comma string (canonical tags, joined). Phase 2
 * (PPL-504) flips this to return the array, enables the server-side shape
 * guard and runs the migration.
 */
export function serializeInterestsForWrite(tags: unknown): string {
  return normalizeInterests(tags).join(', ');
}

